import React, { type FC } from 'react';
import { ProductAttributeInterface } from '../../../../../../../Interface/ProductAttribute';
import { formatCurrencyEur } from '../../../../../../Helper/formatter';
import { useProductsFilter } from '../../../../../../Context/ProductsFilterContext';
import useViewports from '../../../../../../Hooks/useViewports';
import PromoBadge from '../../../../Abstracts/PromoBadge/PromoBadge';
import Tooltip from '../../../../../Tooltip/Tooltip';
import clsx from 'clsx';
import styles from './AttributePrice.module.scss?module';
import DepositInfo from '../../../../Abstracts/DepositInfo/DepositInfo';
import depositImage from '../../../../../../../../public/images/pfand_img.webp';
import { getFlavourDiscount } from '../../../../../../Helper/flavourDiscount';

type AttributePricePropsT = {
  attribute: ProductAttributeInterface;
  className?: string;
  /**
   * Bei false wird nur der Rabatt-/Promo-Badge (Störer) gerendert, aber kein
   * Preis. Genutzt für ausgeloggte Nutzer, die die Clearance-Markierung sehen
   * sollen, aber im B2B-Shop keine Preise erhalten (P-4877, Punkt 1).
   */
  showPrice?: boolean;
};

const AttributePrice: FC<AttributePricePropsT> = ({
  attribute,
  className,
  showPrice = true,
}) => {
  const { filters } = useProductsFilter();
  const { lg, xl, xxl } = useViewports();
  const isDesktop = lg || xl || xxl;
  const discount = getFlavourDiscount(attribute);
  const isClearanceFlavour = discount.isClearance;
  const showRegularPrice =
    (filters.view === 'list' && attribute.product_size.price_show_regular) ||
    isClearanceFlavour;
  const isPriceRed = attribute.product_size.price_is_red || isClearanceFlavour;
  const displayPrice =
    isClearanceFlavour && discount.combinedPrice != null
      ? discount.combinedPrice
      : attribute.product_size.current_price;
  const showTags = isDesktop || filters.view === 'table';
  const singleDepositPrice = attribute.product_size.single_deposit_price;
  const depositUnitsCount = attribute.product_size.deposit_units_count;
  const isDepositProduct = depositUnitsCount > 0 && singleDepositPrice > 0;

  return (
    <div
      className={clsx(
        className,
        styles.PriceContainer,
        filters.view === 'table' && !isDesktop && styles.TablePromoMobile
      )}
    >
      <div className="d-flex justify-content-center align-items-center ms-auto">
        {showPrice && isDepositProduct && filters.view === 'table' && isDesktop ? (
          <Tooltip
            placement="top"
            title={`${depositUnitsCount} x ${formatCurrencyEur(
              singleDepositPrice
            )} Deposit`}
          >
            <div
              className={styles.DepositInfo}
              style={{ transform: 'translateX(-14px)' }}
            >
              <img
                width={35}
                height={35}
                src={depositImage}
                alt="deposit_image"
              />
            </div>
          </Tooltip>
        ) : null}

        {showTags ? (
          <div className={clsx(styles.TagsStack)}>
            {attribute.product_size?.is_ncw_product_size && (
              <a href={attribute.product_size.ncw_link ?? ''} className={styles.NcwBadge}>
                NCW
              </a>
            )}
            {isClearanceFlavour ? (
              <Tooltip title={discount.tooltip} placement="top">
                <div className={styles.ClearanceBadge}>{discount.badgeLabel}</div>
              </Tooltip>
            ) : (
              <PromoBadge
                productSize={attribute.product_size}
                hasStock={attribute.has_stock}
              />
            )}
          </div>
        ) : null}
        {showPrice && displayPrice != null ? (
          <div className={clsx(styles.price)}>
            {showRegularPrice && attribute.product_size.price_regular != null ? (
              <small className="fst-italic text-decoration-line-through">
                {formatCurrencyEur(attribute.product_size.price_regular)}
              </small>
            ) : null}
            <div
              className={[
                'fw-bold fst-italic',
                isPriceRed ? 'text-danger' : '',
              ].join(' ')}
            >
              {formatCurrencyEur(displayPrice)}
            </div>
          </div>
        ) : null}
      </div>
      {showPrice && isDepositProduct && filters.view === 'list' && isDesktop ? (
        <div className={clsx(styles.DepositInfo)}>
          <img width={35} height={35} src={depositImage} alt="deposit_image" />
          <DepositInfo unitsCount={depositUnitsCount} price={singleDepositPrice} />
        </div>
      ) : null}

      {showPrice && isDepositProduct && filters.view === 'table' && !isDesktop ? (
        <div className={clsx(styles.DepositInfo, 'ms-auto')}>
          <img width={35} height={35} src={depositImage} alt="deposit_image" />
          <DepositInfo unitsCount={depositUnitsCount} price={singleDepositPrice} />
        </div>
      ) : null}
    </div>
  );
};

export default AttributePrice;
